import React from "react";
import { Link } from "react-router-dom";
import { useTournament } from "@/lib/TournamentContext";
import {
  BookOpen, LayoutDashboard, CalendarDays, Trophy, Users, ChevronRight,
  Target, Shield, Crown, Star, HelpCircle, Flame, Zap,
} from "lucide-react";
import { UnderdogLegend } from "@/components/app/UnderdogBadge";

const Section = ({ icon: Icon, title, children, testid }) => (
  <section className="surface-card p-6 mb-4" data-testid={testid}>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 bg-primary-accent flex items-center justify-center">
        <Icon size={18} />
      </div>
      <h2 className="font-display text-2xl uppercase tracking-tight">{title}</h2>
    </div>
    {children}
  </section>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
    <span className="text-sm text-zinc-300">{label}</span>
    <span className="font-display text-lg text-primary-accent tabular-nums">{value}</span>
  </div>
);

export default function Rules() {
  const { active } = useTournament();
  if (!active) return null;
  const cfg = active.config || {};
  const comp = active.competitions || {};
  const isLeague = active.format === "league";
  const period = isLeague ? "gameweek" : "matchday";
  const Period = isLeague ? "Gameweek" : "Matchday";

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)] max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <BookOpen size={22} className="text-primary-accent" />
        <div>
          <div className="label-tag">How It Works</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase">Rules & Scoring</h1>
        </div>
      </div>
      <p className="text-zinc-400 mb-8 max-w-3xl">
        Everything below is pulled live from the current tournament configuration —{" "}
        <span className="text-white font-display">{active.name}</span>. Whenever an admin tweaks scoring, toggles competitions,
        or changes settings, this page updates automatically.
      </p>

      {/* NAVIGATION */}
      <Section icon={LayoutDashboard} title="Navigating the App" testid="rules-navigation">
        <ul className="space-y-3 text-sm">
          {[
            { to: "/dashboard", icon: LayoutDashboard, name: "Dashboard", desc: "Your snapshot — total points, predictions, streaks, upcoming matches and top predictors." },
            { to: "/fixtures", icon: CalendarDays, name: "Fixtures & Results", desc: "Schedule and final scores, filterable by stage and group." },
            { to: "/predict", icon: Target, name: "Competitions", desc: "All five competitions (whichever are enabled) live here. The first tab — Main League — opens by default." },
            { to: "/leaderboard", icon: Trophy, name: "Leaderboard", desc: "Individual is the main board (ranked by total points). Filter by stage. The Groups board ranks every group by average score, and there's a Survivor board too." },
            { to: "/profile", icon: Users, name: "Groups & Profile", desc: "Join or leave groups, set your display name and favourite team. Group standings live on the Leaderboard's Groups tab." },
            { to: "/profile", icon: Shield, name: "Profile", desc: "Update your display name, favourite team, and group affiliation." },
          ].map((row) => (
            <li key={row.to} className="flex items-start gap-3">
              <row.icon size={16} className="text-primary-accent mt-0.5" />
              <div>
                <Link to={row.to} data-testid={`rule-link-${row.to.replace("/", "")}`} className="font-display text-base tracking-wider hover:text-primary-accent inline-flex items-center gap-1">
                  {row.name} <ChevronRight size={12} />
                </Link>
                <div className="text-zinc-400">{row.desc}</div>
              </div>
            </li>
          ))}
        </ul>
      </Section>

      {/* MAIN LEAGUE */}
      {comp.main_league && (
        <Section icon={Target} title="Main Prediction League" testid="rules-main-league">
          <p className="text-zinc-400 text-sm mb-4">
            Predict the final scoreline of every match before kickoff. Predictions lock at kickoff time.
            Points are awarded based on how close your prediction is.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Row label="Exact scoreline" value={`+${cfg.pts_exact_score}`} />
            <Row label="Correct goal difference (right margin)" value={`+${cfg.pts_correct_gd}`} />
            <Row label="Correct result only (right winner, wrong margin)" value={`+${cfg.pts_correct_result}`} />
            <Row label="Underdog win bonus" value="+2 to +8" />
            <Row label="Underdog draw bonus (correct draw)" value="half (rounded up)" />
          </div>
          <div className="mt-3 text-xs text-zinc-400">
            Tiers are <span className="text-white">not added</span> — each prediction earns only its single best tier (Exact &gt; GD &gt; Result). The underdog bonus stacks on top when you correctly call an upset win; if the underdog instead <span className="text-white">draws</span> the favourite and you called the draw, you earn <span className="text-white">half</span> the bonus (rounded up). Captain and Power Play ({cfg.powerplay_multiplier}×) each apply once per {period}.
          </div>
          <div className="mt-2 text-xs text-zinc-400">
            On the Predict page, matches are grouped under <span className="text-white">{period} dividers</span> — each shows a status chip (<span className="text-green-400">Open</span> / <span className="text-yellow-300">In Play</span> / <span className="text-primary-accent">Locked</span>) and how many you have picked. Every match card is tagged with its {period} (e.g. <span className="text-white">{isLeague ? "GW1" : "MD1"}</span>).
          </div>
          <div className="mt-4">
            <UnderdogLegend elo={isLeague} />
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-yellow-400/50 p-3">
              <div className="flex items-center gap-2 mb-1">
                <Star size={14} className="text-yellow-400" />
                <span className="font-display text-base">Captain</span>
              </div>
              <div className="text-xs text-zinc-400">Mark one prediction per {period} as captain — its points are multiplied by <span className="text-primary-accent">{cfg.captain_multiplier}×</span>.</div>
            </div>
            <div className="border border-[var(--accent)]/60 p-3">
              <div className="flex items-center gap-2 mb-1">
                <Zap size={14} className="text-[var(--accent)]" />
                <span className="font-display text-base">Power Play</span>
              </div>
              <div className="text-xs text-zinc-400">Use once per {period}. Multiplies that match&apos;s points by <span className="text-primary-accent">{cfg.powerplay_multiplier}×</span>. Stacks with captain.</div>
            </div>
            {isLeague && cfg.super_jinx_enabled && (
              <div className="border border-purple-400/60 p-3 md:col-span-2" data-testid="rules-super-jinx">
                <div className="flex items-center gap-2 mb-1">
                  <Zap size={14} className="text-purple-400" />
                  <span className="font-display text-base">Super Jinx</span>
                </div>
                <div className="text-xs text-zinc-400">
                  A power chip you can play <span className="text-white">once per half-season</span>. When active for a gameweek, <span className="text-white">every prediction you make that gameweek</span> is multiplied by <span className="text-primary-accent">{cfg.super_jinx_multiplier}×</span> — stacking on top of Captain and Power Play. Toggle it on the gameweek divider on the Predict page <span className="text-white">before kickoff</span>; it locks once the gameweek starts.
                </div>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* SURVIVOR */}
      {comp.survivor && (
        <Section icon={Shield} title="Survivor Challenge" testid="rules-survivor">
          {isLeague ? (
            <>
              <p className="text-zinc-400 text-sm mb-3">
                Each gameweek, pick <span className="text-white">{cfg.survivor_picks_per_matchday ?? 3} teams</span> (one team per match) you think will win. Lock them in before the gameweek starts. This is a <span className="text-white">weekly points</span> format — <span className="text-white">there is no elimination</span>, so you play every gameweek of the season.
              </p>
              <ul className="text-sm text-zinc-300 list-disc list-inside space-y-1">
                <li>A picked team that <span className="text-primary-accent">draws or loses</span> is a <span className="text-primary-accent font-display">strike</span> for that gameweek.</li>
                <li>Survive with up to <span className="text-primary-accent">{cfg.survivor_weekly_strikes_allowed ?? 1} strike{(cfg.survivor_weekly_strikes_allowed ?? 1) === 1 ? "" : "s"}</span> and you earn <span className="text-primary-accent">+{cfg.survivor_survive_points ?? 7} points</span> for the gameweek.</li>
                <li>More strikes than allowed = <span className="text-primary-accent">struck out</span> that week (0 points) — but you carry on next gameweek.</li>
                <li>Teams are <span className="text-white">reusable</span> across gameweeks (no season-long lock).</li>
                <li>You may <span className="text-white">change your picks</span> freely until the gameweek locks.</li>
              </ul>
              <div className="mt-3 text-xs text-zinc-400">
                The Survivor leaderboard ranks players by <span className="text-white">total survival points</span> and weeks survived across the season — separate from the main points leaderboard.
              </div>
            </>
          ) : (
            <>
              <p className="text-zinc-400 text-sm mb-3">
                Pick <span className="text-white">{cfg.survivor_picks_per_matchday ?? 3} teams every matchday</span> (one team per match) that you think will win — in the knockouts you pick the same number per round (or fewer if the round has fewer matches). You must lock in all of them before the matchday locks.
              </p>
              <ul className="text-sm text-zinc-300 list-disc list-inside space-y-1">
                <li>A picked team that <span className="text-primary-accent">draws or loses</span> is a <span className="text-primary-accent font-display">strike</span>.</li>
                <li>Reach <span className="text-primary-accent">{cfg.survivor_strikes_allowed ?? 2} strikes</span> and you are eliminated — strikes carry across the whole tournament.</li>
                <li>You <span className="text-primary-accent">cannot reuse a team</span> anywhere in the tournament.</li>
                <li>You may <span className="text-white">change your picks</span> freely until the matchday locks.</li>
                <li>The last players standing — fewest strikes, most correct picks — win the Survivor crown.</li>
              </ul>
              <div className="mt-3 text-xs text-zinc-400">
                Your Dashboard tracks Survivor in two parts — <span className="text-white">Group</span> (turns green once you have locked in all 3 group matchdays) and <span className="text-white">KO</span> (the knockout rounds).
              </div>
            </>
          )}
        </Section>
      )}

      {/* BRACKET */}
      {comp.bracket && (
        <Section icon={Crown} title="Knockout Bracket" testid="rules-bracket">
          <p className="text-zinc-400 text-sm mb-3">
            <span className="text-white">Step 1 — Predict group standings.</span> For each of the 12 groups, predict who finishes 1st, 2nd, 3rd, and 4th.
            <br/><span className="text-white">Step 2 — Pick 8 best 3rd-placed teams</span> to advance to the Round of 32 (they fill T1–T8 in the bracket).
            <br/><span className="text-white">Step 3 — Visual bracket.</span> Pick winners from R32 → R16 → QF → SF → Final, with both halves merging at the Final.
          </p>
          <div className="text-xs text-zinc-400 mb-3">
            The whole bracket <span className="text-white">locks at the tournament kickoff</span> — each group shows an <span className="text-green-400">Open</span> / <span className="text-primary-accent">Locked</span> indicator. Your bracket chip on the Dashboard turns green once you have picked a champion.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Row label="Correct 1st in group" value={`+${cfg.pts_group_first}`} />
            <Row label="Correct 2nd in group" value={`+${cfg.pts_group_second}`} />
            <Row label="Correct 3rd in group" value={`+${cfg.pts_group_third}`} />
            <Row label="Correct 4th in group" value={`+${cfg.pts_group_fourth}`} />
            <Row label="Correct best-3rd advancing" value={`+${cfg.pts_best_third}`} />
            <Row label="Per correct R32 pick" value={`+${cfg.pts_r32}`} />
            <Row label="Per correct R16 pick" value={`+${cfg.pts_r16}`} />
            <Row label="Per correct QF pick" value={`+${cfg.pts_qf}`} />
            <Row label="Per correct SF pick" value={`+${cfg.pts_sf}`} />
            <Row label="Correct Champion" value={`+${cfg.pts_final}`} />
          </div>
        </Section>
      )}

      {/* FANTASY */}
      {comp.fantasy && (
        <Section icon={Star} title="Fantasy XI" testid="rules-fantasy">
          <p className="text-zinc-400 text-sm mb-3">
            Build a squad of <span className="text-white">11 players</span> within budget. Earn points whenever your
            players perform across the tournament.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Row label="Squad size" value="11" />
            <Row label="Budget cap" value={cfg.fantasy_budget} />
            <Row label="Max players per nation" value={cfg.max_per_nation} />
            <Row label="Goal scored" value={`+${cfg.pts_goal}`} />
            <Row label="Assist" value={`+${cfg.pts_assist}`} />
            <Row label="Clean sheet (GK/DEF)" value={`+${cfg.pts_clean_sheet}`} />
            <Row label="Save (GK)" value={`+${cfg.pts_save}`} />
            <Row label="Yellow card" value={cfg.pts_yellow} />
            <Row label="Red card" value={cfg.pts_red} />
          </div>
          <div className="mt-3 text-xs text-zinc-500">
            Pick a captain — they earn {cfg.captain_multiplier}× points for the match.
          </div>
        </Section>
      )}

      {/* STREAK */}
      {comp.streak && (
        <Section icon={Flame} title="Prediction Streak" testid="rules-streak">
          <p className="text-zinc-400 text-sm">
            Stack consecutive correct results (W/D/L). Your current and longest streak are tracked on the leaderboard.
            No points awarded — this is for bragging rights and the Streak leaderboard.
          </p>
        </Section>
      )}

      {/* BONUS */}
      {comp.bonus_questions && (
        <Section icon={HelpCircle} title="Bonus Questions" testid="rules-bonus">
          <p className="text-zinc-400 text-sm">
            Tournament-long predictions — Golden Boot, eventual winner, dark horse, etc. Pick once, lock in,
            and earn the question&apos;s points if you&apos;re right when the tournament ends.
          </p>
        </Section>
      )}

      {/* LEAGUE TABLES & LEADERBOARDS (league only) */}
      {isLeague && (
        <Section icon={Trophy} title="League Table & Leaderboards" testid="rules-league-boards">
          <p className="text-zinc-400 text-sm mb-3">
            This is a <span className="text-white">single round-robin league</span> across {cfg.half_season_split ? `${cfg.half_season_split * 2}` : "all"} gameweeks. The Standings page shows one live table (Win 3 / Draw 1 / Loss 0), with ties broken by head-to-head, then goal difference, goals for and against.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mb-3">
            <Row label="Top zone highlighted (e.g. CL)" value={`Top ${cfg.cl_zone ?? 5}`} />
            <Row label="Relegation zone highlighted" value={`Bottom ${cfg.relegation_zone ?? 3}`} />
            <Row label="Half-season split" value={`GW ${cfg.half_season_split ?? 19}`} />
          </div>
          <div className="text-xs text-zinc-400 space-y-2">
            <p>
              The Leaderboard has a <span className="text-white">Board</span> dropdown so you can rank players over the <span className="text-white">whole season</span>, by <span className="text-white">half-season</span> (first half = GW1–{cfg.half_season_split ?? 19}, second half after), or <span className="text-white">month by month</span>.
            </p>
            {cfg.club_groups_enabled && (
              <p>
                A <span className="text-white">Clubs</span> tab ranks every club by its supporters&apos; scores (method: <span className="text-zinc-300">{(cfg.club_scoring_method || "average").replace("_", " ").toUpperCase()}</span>). Pick your <span className="text-white">favourite club</span> on your Profile to join its club leaderboard automatically.
              </p>
            )}
            <p>
              <span className="text-white">Elo ratings</span> power the underdog bonus and evolve through the season: after every gameweek completes, each team&apos;s rating shifts based on results and goal margin — so today&apos;s upset bonus reflects current form. Live ratings are shown on the Standings table.
            </p>
          </div>
        </Section>
      )}

      {/* GROUPS */}
      <Section icon={Users} title="Groups & Affiliations" testid="rules-groups">
        <p className="text-zinc-400 text-sm mb-3">
          You can represent <span className="text-white">one or more</span> groups. Your individual points contribute to
          each one&apos;s group standing.
        </p>
        <Row label="Group scoring method" value={(cfg.group_scoring_method || "average").replace("_", " ").toUpperCase()} />
        <Row label="Affiliations locked" value={cfg.affiliations_locked ? "YES" : "NO"} />
        <div className="mt-3 text-xs text-zinc-500">
          Method explainer:&nbsp;
          <span className="text-zinc-300">AVERAGE (default)</span> = total group points ÷ members;&nbsp;
          <span className="text-zinc-300">TOP 3 / TOP 5</span> sums the best N members&apos; points;&nbsp;
          <span className="text-zinc-300">SUM</span> adds every member.
          {cfg.affiliation_lock_at_start ? " Affiliations auto-lock once the tournament begins." : ""}
        </div>
      </Section>

      <div className="text-xs text-zinc-500 text-center mt-8">
        Rules generated from live tournament config · {active.name} · {active.short_code}
      </div>
    </div>
  );
}
